/**
 * Timezone-aware date utilities for Staycasa Cloud OS.
 * All functions accept a timezone string (e.g. "Europe/London").
 * Default fallback is "Europe/London" if no timezone is provided.
 */

export const DEFAULT_TZ = "Europe/London";

/**
 * Get the current date/time in the given timezone as a Date object.
 * Note: the Date object is still UTC internally — use orgToYMD for display.
 */
export function orgNow(tz: string = DEFAULT_TZ): Date {
  return new Date(new Date().toLocaleString("en-US", { timeZone: tz }));
}

/**
 * Convert a Date to YYYY-MM-DD string in the given timezone.
 */
const _fmtCache = new Map<string, Intl.DateTimeFormat>();
function getDateFmt(tz: string): Intl.DateTimeFormat {
  if (!_fmtCache.has(tz)) {
    _fmtCache.set(tz, new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    }));
  }
  return _fmtCache.get(tz)!;
}

export function orgToYMD(d: Date, tz: string = DEFAULT_TZ): string {
  const fmt = getDateFmt(tz);
  const parts = fmt.formatToParts(d);
  const y = parts.find(p => p.type === "year")?.value ?? "";
  const m = parts.find(p => p.type === "month")?.value ?? "";
  const dd = parts.find(p => p.type === "day")?.value ?? "";
  return `${y}-${m}-${dd}`;
}

/**
 * Check if a Date is today in the given timezone.
 */
export function orgIsToday(d: Date, tz: string = DEFAULT_TZ): boolean {
  return orgToYMD(d, tz) === orgToYMD(new Date(), tz);
}

/**
 * Get today's YYYY-MM-DD string in the given timezone.
 */
export function orgTodayKey(tz: string = DEFAULT_TZ): string {
  return orgToYMD(new Date(), tz);
}

/**
 * Get milliseconds until next midnight in the given timezone.
 * Used to schedule "today" refreshes.
 */
export function msUntilMidnight(tz: string = DEFAULT_TZ): number {
  const now = new Date();
  const todayStr = orgToYMD(now, tz);
  const [y, m, d] = todayStr.split("-").map(Number);
  // Next midnight in UTC: construct next day in org timezone
  const nextMidnight = new Date(new Date(`${y}-${String(m).padStart(2,"0")}-${String(d+1).padStart(2,"0")}T00:00:00`).toLocaleString("en-US", { timeZone: tz }));
  return nextMidnight.getTime() - now.getTime() + 60000; // +1min buffer
}

/**
 * Convert a property-local calendar date (YYYY-MM-DD) into the UTC instant
 * of local midnight on that date in the given timezone.
 *
 * Intended for UK property-local dates (default Europe/London). Robust across
 * DST transitions for the UK because DST changeovers happen at 01:00 UTC,
 * not local midnight. Not validated for timezones where DST transitions
 * occur exactly at local midnight — if Staycasa expands beyond UK properties,
 * migrate to `Temporal` or a timezone library.
 *
 * Used by `lib/hfSync.ts` `isLastMinute()` to compare a UTC `bookedAt`
 * timestamp against the local-midnight start of the check-in date.
 *
 * @param dateStr  'YYYY-MM-DD' interpreted as a property-local calendar date
 * @param tz       IANA timezone string (default Europe/London)
 * @returns        Date representing the UTC instant of local midnight on that date
 */
export function localMidnightAsUtc(
  dateStr: string,
  tz: string = DEFAULT_TZ
): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) {
    throw new Error(`localMidnightAsUtc: invalid date string '${dateStr}'`);
  }

  // First guess: midnight UTC on that calendar date
  const guess = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));

  // Format the guess back in the target timezone to discover its UTC offset
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone:  tz,
    year:      "numeric",
    month:     "2-digit",
    day:       "2-digit",
    hour:      "2-digit",
    minute:    "2-digit",
    second:    "2-digit",
    hour12:    false,
    hourCycle: "h23",
  }).formatToParts(guess);

  const part = (type: string): number =>
    Number(parts.find(p => p.type === type)?.value ?? 0);

  const localY = part("year");
  const localM = part("month");
  const localD = part("day");
  let   localH = part("hour");
  const localMin = part("minute");
  const localS = part("second");

  // Defensive: some runtimes can still emit '24' for midnight despite
  // hourCycle: 'h23'. Treat as 0 (same calendar day per Intl semantics).
  if (localH === 24) localH = 0;

  // Build the local-clock reading as if it were a UTC instant
  const localAsUtc = Date.UTC(localY, localM - 1, localD, localH, localMin, localS);

  // Offset (ms) = how far the local clock is ahead of UTC at this moment
  // e.g. BST = +1h → localAsUtc is 1h ahead of guess
  const offsetMs = localAsUtc - guess.getTime();

  // True UTC instant for local midnight = UTC midnight minus that offset
  // BST example: local 00:00 = UTC 23:00 the previous day, so subtract +1h
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0) - offsetMs);
}

/**
 * Interpret a local calendar date + HH:MM wall-clock time in the given
 * timezone and return the corresponding UTC Date.
 *
 * Example: ("2026-08-25", "09:00", "Europe/London") during BST returns a
 * Date whose UTC value is 2026-08-25T08:00:00Z (09:00 BST = 08:00 UTC).
 */
export function parseOrgLocalDateTime(
  dateStr: string,
  timeStr: string,
  tz: string = DEFAULT_TZ
): Date {
  const midnight = localMidnightAsUtc(dateStr, tz);
  const [h, m] = timeStr.split(":").map(Number);
  return new Date(midnight.getTime() + ((h || 0) * 60 + (m || 0)) * 60_000);
}

/**
 * Parse an offset-less Hostfully datetime ("2026-05-13T16:00:00" or
 * "2026-05-13") into a Date whose UTC components equal the input's
 * wall-clock components.
 *
 * Used by lib/hfSync.ts for HF Mirror datetime columns:
 *  - `timestamp without time zone` columns (checkIn/Out/arrival/
 *    departureLocalDateTime, HFOrderAdjustmentItem.createdUtcDateTime):
 *    stores the wall-clock verbatim regardless of Node process TZ.
 *  - `timestamptz` offset-less *UtcDateTime / bookedUtcDateTime columns:
 *    Hostfully sends these as true UTC (verified 2026-05-15 via the
 *    Booking.com 24h cancellation-grace cross-reference), so parsing the
 *    components as UTC is correct.
 *
 * Does NOT apply any Europe/London conversion — Hostfully *UtcDateTime
 * fields are UTC. (The Phase B.3 backfill applied a spurious London->UTC
 * shift; that is a separate documented follow-up, not this helper's concern.)
 *
 * Returns null for null/undefined/unparseable input. Caller decides whether
 * a null is tolerable (nullable column) or an error (NOT NULL column).
 */
export function parseNaiveAsUtc(s: string | null | undefined): Date | null {
  if (s == null) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}):(\d{2}))?/.exec(s.trim());
  if (!m) return null;
  const [, y, mo, d, hh = "0", mi = "0", ss = "0"] = m;
  return new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mi, +ss));
}

/**
 * Format a Date as naive whole-second UTC ISO string, matching the format
 * Hostfully's /api/v3/orders endpoint accepts for `updatedSince`.
 *
 * Examples:
 *   formatNaiveSecondsUtc(new Date("2026-05-16T15:29:47.123Z"))
 *     → "2026-05-16T15:29:47"
 *
 * Drops milliseconds (truncates, not rounds). Drops Z suffix.
 * Empirically verified 2026-05-17 against live Hostfully API:
 *   updatedSince=2026-05-16T15:29:47 is INCLUSIVE at whole-second resolution.
 *
 * Used by lib/hfSync.ts extractOrdersPhase when converting
 * CoreListing.lastOrderSyncUtc to the string parameter for
 * listOrdersByPropertyUid.
 */
export function formatNaiveSecondsUtc(date: Date): string {
  return date.toISOString().slice(0, 19);
}
